import { GameID, SeasonID, SeasonWeekID, SyncIDBStores, TeamID, WeekID } from 'types';

export enum FirebaseCollections {
  /** Maps to FE `gameSchedule` store */
  Schedule = 'schedule',
  /** Maps to FE `gameStats` store */
  MatchResults = 'matchresults',
  /** Maps to FE `teamWeeklyStats` store */
  TeamMatchResults = 'teammatchresults',
  /** Maps to FE `teamInfo` store */
  Teams = 'teams',
  /** Maps to FE `seasonStandings` store */
  Standings = 'standings',
  /** Maps to FE `weeklyRanks` store */
  Rankings = 'rankings',
  /** Maps to FE `predictions` store */
  Predictions = 'predictions',
  /** Maps to FE `headToHead` store */
  HeadToHead = 'headtohead',
  /**`TeamsOnByeWeek` is not a collection, it's a part of `stats/${seasonID}` document under `byeWeeks` prop  */
  /** 'syncStatus' is also not a collection, it's a document containing all sync status information for the Firebase */
}

export type SyncFirebaseStoreProps = {
  id: FirebaseCollections;
  lastUpdatedOn: number;
  timestamp: string;
};

export type SyncFirebaseStore = {
  [key in FirebaseCollections]: SyncFirebaseStoreProps;
};

export type SeasonWeekStatus = {
  /** Collection ID, to be used as a unique identifier */
  id: SeasonWeekID;
  /** Season ID (year) */
  seasonID: SeasonID;
  /** Week1 -Week18 */
  weekID: WeekID;
  /** Games scheduled for the week */
  gameIDs?: GameID[];
  /** Teams on a bye week */
  teamsOnBye?: TeamID[];
  /** Indicates if the weekly collections has been completed */
  completed: boolean;
  /** Timestamp of the last update to this record */
  lastUpdatedOn: number;
  /** Timestamp of the last update to this record as a string */
  timestamp: string;
} & SyncFirebaseStore;

export type SeasonCollectionStatus = {
  /** Season ID (year) */
  seasonID: SeasonID;
  /** Array of weekly collection statuses for the season */
  weeks: SeasonWeekStatus[];
  /** Indicates if the entire season's collections have been completed */
  completed: boolean;
  /** Timestamp of the last update to this record */
  lastUpdatedOn: number;
};

export type SyncManagerStatus = {
  /** Document ID, to be used as a unique identifier */
  id: 'SyncManagerStatus';
  /** Array of season collection statuses */
  seasons: SeasonCollectionStatus[];
  /** Timestamp of the last update to this record */
  lastUpdatedOn: number;
  /** in cloud functions this will be `AdminAction`
   *
   * ```
   * export const AdminAction = {
   *   LoadSchedule: 'loadSchedule',
   *   LoadGameScore: 'loadGameScore',
   *   LoadStandings: 'loadStandings',
   *   LoadRankings: 'loadRankings',
   * } as const;
   * export type AdminActionNameT = (typeof AdminAction)[keyof typeof AdminAction];
   * ```
   */
  lastUpdatedBy: string;
  /** Note area in case something needs to be documented */
  notes?: string;
  /** Timestamp of the last update from the server as a string */
  timestamp: string;
};

export type CallbackSyncManagerStatus = (status: SyncManagerStatus | null) => void;

export type DBsInitState = {
  firebaseDBInit: boolean;
  indexDBInit: boolean;
};

export type SyncServerObservation = {
  state: 'idle' | 'connecting' | 'cached' | 'live' | 'missing' | 'error';
  error?: string;
};

export type SyncManagerProps = {
  serverObservation: SyncServerObservation;
  initializing: boolean;
  // Firebase props
  id: 'SyncManagerStatus';
  dbsInitState: DBsInitState;
  seasons: SeasonCollectionStatus[];
  notes: string;
  serverLastUpdatedOn: string;
  beLastUpdatedOn: number;
  // Front end props
  clientLastUpdatedOn: string;
  feLastUpdatedOn: number;
  idbStatus: SyncIDBStores;
};
